import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { iRootDispatch, iRootState } from '../../../../store';
import { AvailableBrokersAndEstatesState } from '../../../../store/types/temp-types';
import { dangerNotification } from '../../../../services/notification';
import { EstatePreregisterListPageProps } from '../..';
import { unmaskCpfOrCnpj } from '../../../../services/masks';
import EstatePreRegisterListPageTable from '../Table';
import { useQuery } from '@tanstack/react-query';
import ViewModal from '../ViewModal';
import { EstatePreregisterState } from '../../../../types/EstatePreregisterState';
import { getEstatesPreregisterList } from '../../../../services/estate';
import { statusOptions, typeOptions } from '../../types';
import { hasPermission } from '../../../../components/AuthorizationContainer';
import { ActionEnum } from '../../../../enums/authz-action.enum';
import { FeatureEnum } from '../../../../enums/authz-feature.enum';
import FilterBar, { FilterField, FilterValues } from '../../../../components/Filter';

type SearchField = 'document' | 'name' | 'address';

type ApiFilters = {
  type?: string;
  argument?: string;
  status?: string;
  franchise?: string;
};

const SEARCH_FIELDS: SearchField[] = ['document', 'name', 'address'];

const initialFilterValues: FilterValues = {
  type: 'document',
  argument: '',
  status: '',
  franchise: ''
};

const toSelectOptions = (
  items: Array<{ value: any; text: string }>
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const normalizeToApiFilter = (values: FilterValues): ApiFilters => {
  const searchType = (values.type as SearchField) || 'document';
  let argument = String(values.argument || '').trim();

  if (searchType === 'document' && argument) {
    argument = unmaskCpfOrCnpj(argument);
  }

  const filter: ApiFilters = {};

  if (argument && SEARCH_FIELDS.includes(searchType)) {
    filter.type = searchType;
    filter.argument = argument;
  }

  if (values.status) filter.status = String(values.status);
  if (values.franchise) filter.franchise = String(values.franchise);

  return filter;
};

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

const EstatePregisterListPage = (props: EstatePreregisterListPageProps) => {
  const { availableBrokersAndEstates } = props;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemSelected, setItemSelected] = useState<EstatePreregisterState | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [userCanDetail, setUserCanDetail] = useState<boolean>(false);
  const [total, setTotal] = useState(0);

  const [appliedFilters, setAppliedFilters] = useState<ApiFilters>(() =>
    normalizeToApiFilter(initialFilterValues)
  );
  const appliedFiltersRef = useRef(appliedFilters);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    const has = hasPermission(ActionEnum.detail, FeatureEnum.preregisters);
    setUserCanDetail(has);
  }, []);

  const franchiseOptions = useMemo(
    () =>
      (availableBrokersAndEstates?.franchises || []).map(item => ({
        value: String(item.value || ''),
        text: String(item.text || '')
      })),
    [availableBrokersAndEstates?.franchises]
  );

  const filterFields: FilterField[] = useMemo(
    () => [
      {
        kind: 'search',
        name: 'search',
        label: 'Pesquisar',
        placeholder: 'Digite...',
        defaultType: 'document',
        types: typeOptions.map(option => ({
          value: String(option.value),
          label: String(option.text)
        })),
        width: 1.6
      },
      {
        kind: 'select',
        name: 'status',
        label: 'Status',
        placeholder: 'Selecionar...',
        options: toSelectOptions(statusOptions),
        getDisplayValue: values => {
          const option = statusOptions.find(
            item => String(item.value) === String(values.status)
          );
          return option ? option.text : undefined;
        }
      },
      {
        kind: 'select',
        name: 'franchise',
        label: 'Franquia',
        placeholder: 'Selecionar...',
        options: toSelectOptions(franchiseOptions),
        getDisplayValue: values => {
          const option = franchiseOptions.find(
            item => String(item.value) === String(values.franchise)
          );
          return option ? option.text : undefined;
        }
      }
    ],
    [franchiseOptions]
  );

  const getData = useCallback(async () => {
    try {
      const filters = {
        page: `${currentPage}`,
        ...appliedFiltersRef.current
      };

      const response = await getEstatesPreregisterList(filters);

      if (currentPage === 1) setTotal(response.count);

      if (response?.page != null && Number(response.page) !== currentPage) {
        setCurrentPage(Number(response.page));
      }

      return response;
    } catch (e) {
      dangerNotification(
        'Oops...',
        'Não foi possível buscar as imobiliárias pré-cadastradas!'
      );
      return { data: [], count: 0, page: currentPage };
    }
  }, [currentPage]);

  const {
    data,
    isFetching: isLoading,
    refetch
  } = useQuery(['getEstatesPreregisterList', currentPage, appliedFilters], getData, {
    enabled: true,
    keepPreviousData: true,
    refetchOnMount: 'always'
  });

  const handleFilterSearch = (values: FilterValues) => {
    setAppliedFilters(normalizeToApiFilter(values));
    setCurrentPage(1);
  };

  const handleFilterClear = () => {
    setCurrentPage(1);
  };

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <FilterBar
          fields={filterFields}
          initialValues={initialFilterValues}
          onSearch={handleFilterSearch}
          onClear={handleFilterClear}
          loading={isLoading}
          syncToUrl
          urlNamespace="estates-preregisters"
        />
      </div>

      <EstatePreRegisterListPageTable
        estates={data?.data}
        isLoading={isLoading}
        currentPage={data?.page}
        setCurrentPage={setCurrentPage}
        getData={refetch}
        dataCount={total}
        handleSelectItem={(item: EstatePreregisterState) => {
          if (userCanDetail) {
            setItemSelected(item);
            setIsViewModalOpen(true);
          }
        }}
      />
      {itemSelected && (
        <ViewModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          estate={itemSelected}
        />
      )}
    </>
  );
};

export default connect(mapState, mapDispatch)(EstatePregisterListPage);
